import { PartnerType } from "./partners/PartnerTypes";
import { ProductTypeEnum, ProductVMType } from "./products/ProductCommandsType";
import { UserType } from "./users/UserTypes";
import { StockCutType } from "util/stockLengths";

export enum InquiryTypeEnum {
  CNC = "CNC",
  PRINT3D = "PRINT3D",
  SHEET = "SHEET",
  FINISH = "FINISH",
  MATERIAL = "MATERIAL",
  // EE-253: the inquiry types mirror the requisition types. Same stock lines
  // as MATERIAL, but the supplier cuts to a part we hold, so the email carries
  // the part's model and drawing - decided by this type, not the requisition.
  PROCESSED_RAW_MATERIAL = "PROCESSED_RAW_MATERIAL",
  CUSTOM = "CUSTOM",
  TOOL = "TOOL",
}

// Both are the material email: lines of stock, described by material and
// measurements, with no quantity column.
export const MATERIAL_INQUIRY_TYPES: readonly InquiryTypeEnum[] = [
  InquiryTypeEnum.MATERIAL,
  InquiryTypeEnum.PROCESSED_RAW_MATERIAL,
];

export const isMaterialInquiry = (type?: InquiryTypeEnum | null): boolean =>
  !!type && MATERIAL_INQUIRY_TYPES.includes(type);

// EE-253: mirrors backend/common/enums/InquiryStatusEnum.py.
//   DRAFT -> APPROVED -> AWAITING_RESPONSE -> PO_SENT -> ORDERED
//         -> PICKUP_DELIVERY -> READY_TO_USE -> CLOSED
// Placing an order is two states, not one: PO_SENT is our purchase order gone
// out to the supplier, ORDERED is the supplier confirming it. They are often
// days apart, and until the second one the order is not a fact.
// CLOSED is the only final state and is reachable from anywhere. Finished and
// cancelled are the same state told apart by `cancelled` on the inquiry: the
// requisitions behind it finishing it closes it as finished, ending it early
// closes it as cancelled. There is no park state any more (PAUSED is gone).
export enum InquiryStatusEnum {
  DRAFT = "DRAFT",
  APPROVED = "APPROVED",
  AWAITING_RESPONSE = "AWAITING_RESPONSE",
  PO_SENT = "PO_SENT",
  ORDERED = "ORDERED",
  PICKUP_DELIVERY = "PICKUP_DELIVERY",
  READY_TO_USE = "READY_TO_USE",
  CLOSED = "CLOSED",
}

export type CreateInquiryType = {
  orderId?: number;
  quoteId?: number;
  partnerId: number;
  type: string;
  deadline?: string;
  deliveryMethod: string;
  status?: InquiryStatusEnum;
  message: string;
  items: CreateInquiryItemType[];
  title?: string;
  // EE-240: free-text partner history, surfaced on the Partner Overview.
  deliveryTime?: string;
  notes?: string;
};

export type RenderInquiryType = {
  id?: number;
  orderId?: number;
  quoteId?: number;
  partnerId: number;
  type: string;
  deadline?: string;
  deliveryMethod: string;
  status?: InquiryStatusEnum;
  message: string;
  items: CreateInquiryItemType[];
  title?: string;
  deliveryTime?: string;
  notes?: string;
};

export type CreateInquiryItemType = {
  productId: number;
  orderItemId?: number | null;
  quoteItemId?: number | null;
  // No price: a line is priced when the purchase order is placed (EE-253),
  // and an update that leaves the price off keeps the one already saved.
  quantity: number;
  // UpdateInquiryItemIM.comment, which update_inquiry writes to
  // INQUIRYITEMS.comment unconditionally - so an update has to send back what
  // the line already had or it is erased. Carries the material/tool spec on a
  // requisition-born line (EE-207).
  comment?: string;
};

export type UpdateInquiryType = {
  state: InquiryStatusEnum;
  // An inquiry created by a requisition submit starts with no supplier, so
  // this is how one gets set - and, sent as null, cleared again (the backend
  // refuses that from Ordered on). Omit to leave it unchanged.
  partnerId?: number | null;
  // Draft only - the backend 409s a title or type change on an inquiry that
  // has already left Draft. Omit to leave unchanged.
  title?: string;
  type?: InquiryTypeEnum;
  deadline?: string;
  deliveryMethod?: string;
  message?: string;
  items?: (UpdateInquiryItemType | CreateInquiryItemType)[];
  deliveryTime?: string;
  notes?: string;
};

export type UpdateInquiryItemType = CreateInquiryItemType & {
  id: number;
};

export type InquiryType = {
  created_by: string;
  created: string;
  id: number;
  partnerId: number | null;
  // Null until a buyer picks a supplier (EE-253).
  partner: PartnerType | null;
  title?: string;
  type: InquiryTypeEnum;
  deliveryDate: string;
  deliveryMethod: string;
  state: InquiryStatusEnum;
  orderId?: number;
  quoteId?: number;
  // The internal Requisition this inquiry belongs to. Since EE-253 an inquiry
  // is created automatically when a requisition is approved, so most rows on
  // the sourcing board have one. Null for an inquiry raised directly.
  requisitionId?: number | null;
  // Every requisition / order / quote this Inquiry buys for (EE-253): an
  // Inquiry composed from the pool can carry several, and then the singles
  // above are null. Empty requisitionIds = raised by hand.
  requisitionIds?: number[];
  orderIds?: number[];
  quoteIds?: number[];
  customer: UserType;
  message: string;
  // The lines with their parts. Detail responses only: the list sends null
  // and the counts below instead - a part per line on every row made a page
  // of 25 inquiries weigh ~13MB. InquiryDialog is seeded with a list row, so
  // it has to cope with null until its own fetch lands.
  items: InquiryItemType[] | null;
  itemCount: number;
  // EE-253: money in øre, excl. VAT, DKK - see util/money. Null until the
  // purchase order prices it. itemsTotalCents sums the priced lines only, and
  // totalCents adds the transport; both are the backend's, not summed here.
  transportCostCents?: number | null;
  itemsTotalCents?: number | null;
  totalCents?: number | null;
  deliveryTime?: string;
  notes?: string;
  // EE-253: when the supplier was last emailed this inquiry, null if never.
  // `state` is not this: it is walked by hand as often as by sending, and a
  // re-send of an inquiry past Sent / Awaiting Response leaves it where it is.
  // Derived from the id, like requisition_no. "#123" alone is
  // indistinguishable from an order or a task id at a glance.
  inquiryNo?: string | null;
  sentAt?: string | null;
  // Something reached this inquiry from the requisition side - a submission,
  // a resubmit, a cancel - that nobody on the buying side has opened since.
  // Mirrors RequisitionType.status_changed_at / status_ack_pending; drawn
  // with the same indicator and counted by the same kind of menu badge.
  statusChangedAt?: string | null;
  statusAckPending?: boolean;
  // The shortlist that precedes `partner` - who was asked, and whether they
  // have been written to. `partner` itself stays the settled choice, so code
  // that only understands one supplier keeps working. Detail responses only;
  // the list omits it (an N+1 across the board otherwise).
  partnerCandidates?: InquiryPartnerCandidateType[] | null;
  // Board-only. The list endpoint cannot afford partnerCandidates or
  // auditTrail per row, so it sends the two things the row actually draws:
  // a capped shortlist with the real count beside it, and the latest note.
  potentialSuppliers?: InquiryRowSupplierType[] | null;
  potentialSupplierCount?: number | null;
  latestNote?: string | null;
  // Board-only too, in place of the lines: lines per product type
  // ({ CNC: 3 }) for the row caption.
  itemTypes?: Partial<Record<ProductTypeEnum, number>> | null;
  // Append-only workflow history. Detail responses only, same reason.
  auditTrail?: InquiryAuditEntryType[] | null;
  // Goods-in rollup over the lines; null before Pickup / Delivery and when
  // closed. Sent on list and detail alike - the board column reads it.
  deliveryProgress?: DeliveryProgressType | null;
  // Only meaningful on CLOSED: true = ended early, false = ran its course.
  cancelled?: boolean;
  // EE-253 P2b, detail only: every requisition whose lines this Inquiry
  // carries or carried. `live` false = all its lines left (taken off the
  // Draft, or the requisition cancelled).
  requisitions?: InquiryRequisitionRefType[] | null;
};

// Mirrors modules/purchasing/application/composition.enquiry_sources.
export type InquiryRequisitionRefType = {
  id: number;
  requisitionNo: string;
  status: string | null;
  live: boolean;
  lineCount: number;
};

/** One requisition line an Inquiry line buys for (INQUIRY_ITEM_ALLOCATIONS). */
export type InquiryItemSourceType = {
  key: string;
  requisitionItemId: number;
  requisitionId: number;
  requisitionNo: string;
  lineNo: number | null;
  salesOrderId: number | null;
  salesOrderNo: string | null;
  required: string;
  requiredAmount: number | null;
  unit: string | null;
  requiredBy: string | null;
  // The requisition was cancelled after the Inquiry left Draft: the line
  // stays with the supplier, but nobody needs it any more.
  cancelled: boolean;
  live: boolean;
  // EE-253 P3: this line's share of the Inquiry line's price and of the
  // transport, in øre. Null until the buyer has entered the prices.
  costShareCents?: number | null;
  transportShareCents?: number | null;
  // On a raw-material line bought in stock lengths: how far the lengths
  // checked over so far cover this source, in mm (need first, served first).
  coverage?: { gotMm: number; needMm: number | null; ready: boolean } | null;
};

/** What a finishing line counts in (EE-253 P3). */
export const SERVICE_UNITS = ["pcs", "lot", "m2", "kg"] as const;
export type ServiceUnitType = (typeof SERVICE_UNITS)[number];
export const SERVICE_UNIT_LABELS: Record<ServiceUnitType, string> = {
  pcs: "pcs",
  lot: "lot",
  m2: "m²",
  kg: "kg",
};

/** An inquiry as the detail endpoint sends it: with its lines. */
export type InquiryDetailType = InquiryType & { items: InquiryItemType[] };

/** A list row has no lines; InquiryDialog is seeded with one. */
export const hasLines = (inquiry: InquiryType): inquiry is InquiryDetailType =>
  inquiry.items != null;

// Mirrors viewmodels/purchasing/PartnerCandidateVM.py. Carries no quoted
// numbers - who was asked and whether they were written to.
export type InquiryRowSupplierType = {
  partnerId: number;
  partnerName: string | null;
  sendEmail: boolean;
  sentAt: string | null;
};

export type InquiryPartnerCandidateType = {
  id: number;
  partner_id: number;
  partner_name: string | null;
  partner_email: string | null;
  requisition_id: number | null;
  inquiry_id: number | null;
  sent_at: string | null;
  // Whether the broadcast mails this one. False for a supplier approached
  // some other way - they stay on the shortlist and stay pickable.
  send_email: boolean;
  // Derived from the inquiry's partner_id rather than stored - one source of
  // truth for the decision.
  is_final: boolean;
};

// Mirrors viewmodels/purchasing/InquiryAuditVM.py.
export type InquiryAuditEntryType = {
  id: number;
  action: string;
  from_status: string | null;
  to_status: string | null;
  comment: string | null;
  actor_id: number | null;
  actor_email: string | null;
  created: string | null;
};

export type InquiryItemType = {
  id: number;
  reqId: number;
  // EE-207: null for Inquiries converted from a raw-material/tool Purchase
  // Inquiry - those lines have no PRODUCTS row, the spec lives in
  // `comment` instead (see InquiryItemVM.py / the conversion controller).
  product: ProductVMType | null;
  // The line total in øre, excl. VAT - null until the purchase order prices it.
  priceCents: number | null;
  // The order / quote line this was taken from, by id only. A line without
  // a quantity of its own (legacy) gets that line's from the backend.
  orderItemId: number | null;
  quoteItemId: number | null;
  quantity: number
  // Returned by InquiryItemVM; carries the material/tool spec for
  // product-less lines converted from a Requisition (EE-207).
  comment: string | null;
  // Goods-in for the Pickup / Delivery step, as nested running totals rather
  // than a split of the line: everything the supplier has announced as
  // collectable (pickup inquiries only - a delivery line keeps 0 there),
  // everything physically here, and everything checked over since it
  // arrived. So readyToUseQty <= receivedQty <= readyQty <= quantity, each
  // one measured against the whole line and none of them adding to another.
  // All 0 until the buyer records something, and every line fully ready to
  // use is what lets the buyer complete the inquiry.
  //
  // Optional because the backend has shipped a payload without them before -
  // InquiryItemVM dropped readyToUseQty on serialisation - and a required
  // type made that invisible here rather than a compile error.
  readyQty?: number;
  receivedQty?: number;
  readyToUseQty?: number;
  // EE-253 P2b, detail only: the requisition line the Inquiry line was built
  // from, and every requisition line it buys for.
  requisitionItemId?: number | null;
  sources?: InquiryItemSourceType[] | null;
  // EE-253 P3. A raw-material line carries its stock key and length;
  // `quantity` is then the number of lengths (1 × everything needed, until
  // the buyer types another). quantityOverridden: the buyer set them, and a
  // rebuild keeps them.
  stockKey?: string | null;
  stockLengthMm?: number | null;
  quantityOverridden?: boolean;
  // Raw material: what the requisition lines need end to end, and how much
  // of it the count and length leave uncut (null: they hold every piece).
  neededMm?: number | null;
  shortMm?: number | null;
  cuts?: StockCutType[] | null;
  // A finishing line's own quantity, with fractions, and its unit.
  serviceQuantity?: number | null;
  serviceUnit?: ServiceUnitType | null;
};

// Rollup of the lines' goods-in counters, computed by the backend. Null
// before Pickup / Delivery and on a closed inquiry - the board draws "-".
export type DeliveryProgressType = {
  total?: number;
  // Nested running totals, each a subset of the one before it:
  // readyToUse <= received <= ready <= total. They do not add up to total,
  // so what is still at the supplier is ready - received, and what has not
  // been checked over yet is received - readyToUse.
  //
  // The exception is a delivery inquiry, where nothing is ever announced for
  // collection: ready stays 0 while received climbs, so ready - received is
  // negative and means nothing. Read deliveryMethod on the inquiry before
  // showing it - see isDeliveryInquiry.
  ready?: number;
  received?: number;
  // The one the inquiry's own status is read from: any of it reads Ready to
  // Use, and all of it lets the buyer complete the inquiry.
  readyToUse?: number;
  // Optional for the same reason as the line counters above: a missing count
  // has to be a compile error at the next reader, not a silent NaN.
};

export type InquiryListType = {
  items: InquiryType[];
  page: number;
  pageSize: number;
  total: number;
};
